import React from 'react';
import {
  SkeletonAvatar,
  SkeletonBlock,
  SkeletonButton,
  SkeletonLine,
  SkeletonMedia,
  SkeletonPill,
} from './Skeleton';
import TableSkeletonRows from './TableSkeletonRows';

export function ArticleSkeleton({ className = '', lines = 8 }) {
  return (
    <div className={['content-skeleton content-skeleton--article', className].filter(Boolean).join(' ')} role="status" aria-live="polite">
      <SkeletonLine width="34%" height={14} />
      <SkeletonLine width="72%" height={22} />
      {Array.from({ length: lines }, (_, index) => (
        <SkeletonLine
          key={`article-skeleton-line-${index}`}
          width={index % 4 === 3 ? '58%' : index % 3 === 1 ? '86%' : '94%'}
          height={12}
        />
      ))}
    </div>
  );
}

export function StatGridSkeleton({ cardClassName = '', count = 4 }) {
  return (
    <div className="admin-action-log__stats-grid" aria-hidden="true">
      {Array.from({ length: count }, (_, index) => (
        <div key={`stat-grid-skeleton-${index}`} className={cardClassName || 'admin-action-log__stat-card'}>
          <SkeletonLine width={index === 0 ? '56%' : '68%'} height={11} />
          <SkeletonLine width={index === 2 ? 92 : 64} height={28} />
        </div>
      ))}
    </div>
  );
}

export function ChartGridSkeleton({ count = 4 }) {
  return (
    <div className="admin-action-log__chart-grid" aria-hidden="true">
      {Array.from({ length: count }, (_, index) => (
        <div key={`chart-grid-skeleton-${index}`} className="admin-action-log__chart-card">
          <div className="admin-action-log__chart-head">
            <SkeletonLine width={index === 0 ? 112 : 92} height={12} />
            <SkeletonPill width={64} height={20} />
          </div>
          {[0, 1, 2, 3].map((rowIndex) => (
            <div key={`chart-row-skeleton-${index}-${rowIndex}`} className="content-skeleton__bar-row">
              <SkeletonLine width={rowIndex === 1 ? '42%' : '56%'} height={11} />
              <SkeletonBlock className="content-skeleton__bar" />
              <SkeletonLine width={34} height={11} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export function ActionLogTableSkeleton({ rows = 8 }) {
  const columns = [
    { key: 'date', variant: 'stack', primaryWidth: '72%', secondaryWidth: '44%' },
    { key: 'user', variant: 'profile', size: 28, primaryWidth: '58%', secondaryWidth: '38%' },
    { key: 'device', variant: 'line', width: '64%' },
    { key: 'action', variant: 'stack', primaryWidth: '66%', secondaryWidth: '46%' },
    { key: 'target', variant: 'stack', primaryWidth: '76%', secondaryWidth: '42%' },
  ];
  return (
    <div className="admin-action-log__table-wrap" aria-hidden="true">
      <table className="admin-action-log__table">
        <thead>
          <tr>
            <th style={{ width: 170 }}>日時</th>
            <th style={{ width: 180 }}>ユーザー</th>
            <th style={{ width: 120 }}>端末</th>
            <th>アクション</th>
            <th style={{ width: 260 }}>対象</th>
          </tr>
        </thead>
        <tbody>
          <TableSkeletonRows rows={rows} columns={columns} />
        </tbody>
      </table>
    </div>
  );
}

export function AnnouncementListSkeleton({ rows = 5 }) {
  return (
    <div className="announcement-admin__table" aria-hidden="true">
      {Array.from({ length: rows }, (_, index) => (
        <div key={`announcement-skeleton-${index}`} className="announcement-admin__row">
          <div className="announcement-admin__row-main">
            <SkeletonLine width={index === 1 ? '54%' : '76%'} height={15} />
            <SkeletonLine width={index === 3 ? '42%' : '62%'} height={11} />
          </div>
          <div className="announcement-admin__actions">
            <SkeletonButton width={58} height={32} />
            <SkeletonButton width={58} height={32} />
          </div>
        </div>
      ))}
    </div>
  );
}

export function CardListSkeleton({ rows = 5, className = '', showTrailing = true }) {
  return (
    <div className={['content-skeleton__list', className].filter(Boolean).join(' ')} role="status" aria-live="polite">
      {Array.from({ length: rows }, (_, index) => (
        <div key={`card-list-skeleton-${index}`} className="content-skeleton__list-item" aria-hidden="true">
          <div>
            <SkeletonLine width={index === 1 ? '54%' : '76%'} height={14} />
            <SkeletonLine width={index === 3 ? '42%' : '62%'} height={10} />
          </div>
          {showTrailing ? <SkeletonPill width={index === 2 ? 54 : 72} height={20} /> : null}
        </div>
      ))}
    </div>
  );
}

export function AvatarChipGridSkeleton({ className = '', rows = 12 }) {
  return (
    <div className={['content-skeleton__chip-grid', className].filter(Boolean).join(' ')} aria-hidden="true">
      {Array.from({ length: rows }, (_, index) => (
        <div key={`avatar-chip-skeleton-${index}`} className="content-skeleton__avatar-chip">
          <SkeletonAvatar size={30} />
          <SkeletonLine width={index % 3 === 1 ? 86 : 118} height={12} />
        </div>
      ))}
    </div>
  );
}

export function EventSearchResultSkeleton({ rows = 5 }) {
  return (
    <div className="admin-broadcast-event-search-results" aria-hidden="true">
      {Array.from({ length: rows }, (_, index) => (
        <div key={`event-search-skeleton-${index}`} className="admin-broadcast-event-result-row">
          <SkeletonLine width={index === 1 ? '52%' : '72%'} height={14} />
          <SkeletonLine width={index === 2 ? '46%' : '64%'} height={11} />
        </div>
      ))}
    </div>
  );
}

export function PublicEventCardSkeleton({ count = 4 }) {
  return (
    <div className="avant-events-slider" role="status" aria-live="polite">
      <div className="avant-events-track" aria-hidden="true">
        {Array.from({ length: count }, (_, index) => (
          <article key={`public-event-skeleton-${index}`} className="avant-event-card">
            <div className="avant-event-card__image">
              <SkeletonMedia />
            </div>
            <div className="avant-event-card__content">
              <span className="avant-event-card__pref">
                <SkeletonLine width={index === 1 ? 72 : 92} height={11} />
              </span>
              <h4 className="avant-event-card__name">
                <SkeletonLine width={index === 2 ? '62%' : '82%'} height={16} />
              </h4>
              <div className="avant-event-card__meta">
                <SkeletonPill width={72} height={20} />
                <SkeletonPill width={86} height={20} />
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

export function ConciergeStatusSkeleton() {
  return (
    <div className="concierge-public">
      <div className="concierge-public__shell">
        <div className="concierge-public__hero" aria-hidden="true">
          <SkeletonLine width={240} height={24} />
          <SkeletonLine width="82%" height={13} />
          <SkeletonLine width="64%" height={13} />
        </div>
        <div className="concierge-status" role="status" aria-live="polite">
          <SkeletonPill width={132} height={30} />
          <SkeletonLine width="72%" height={13} />
          <div className="concierge-status__grid" aria-hidden="true">
            {[0, 1, 2, 3].map((index) => (
              <div key={`concierge-status-skeleton-item-${index}`} className="concierge-status__item">
                <SkeletonLine width={72} height={10} />
                <SkeletonLine width={index % 2 === 0 ? '76%' : '54%'} height={13} />
              </div>
            ))}
          </div>
          <div className="concierge-status__actions" aria-hidden="true">
            <SkeletonButton width={168} height={40} />
            <SkeletonButton width={148} height={40} />
          </div>
        </div>
      </div>
    </div>
  );
}

export function CalendarPanelSkeleton({ rows = 3 }) {
  return (
    <div className="calendar-events-panel__skeleton-list" aria-hidden="true">
      {Array.from({ length: rows }, (_, index) => (
        <div key={`calendar-panel-skeleton-${index}`} className="calendar-events-panel__skeleton-item">
          <SkeletonMedia className="calendar-events-panel__skeleton-media" />
          <div className="calendar-events-panel__skeleton-body">
            <SkeletonLine width={index === 1 ? '58%' : '78%'} height={14} />
            <SkeletonLine width={index === 2 ? '42%' : '64%'} height={11} />
            <SkeletonPill width={82} height={20} />
          </div>
        </div>
      ))}
    </div>
  );
}

export function CalendarGridSkeleton() {
  const weekdays = ['日', '月', '火', '水', '木', '金', '土'];
  return (
    <div className="calendar-card" role="status" aria-live="polite">
      <div className="calendar-card__header calendar-card__header--compact" aria-hidden="true">
        <div className="calendar-month-nav calendar-month-nav--center">
          <SkeletonLine width={140} height={18} />
        </div>
      </div>
      <div className="calendar-weekdays" aria-hidden="true">
        {weekdays.map((label, index) => (
          <div
            key={`calendar-skeleton-weekday-${index}`}
            className={[
              'calendar-weekday',
              index === 0 ? 'calendar-weekday--sun' : '',
              index === 6 ? 'calendar-weekday--sat' : '',
            ].filter(Boolean).join(' ')}
          >
            {label}
          </div>
        ))}
      </div>
      <div className="calendar-grid" aria-hidden="true">
        {Array.from({ length: 42 }, (_, index) => (
          <div key={`calendar-skeleton-cell-${index}`} className="calendar-cell">
            <div className="calendar-cell__date">
              <SkeletonLine width={18} height={12} />
            </div>
            <div className="calendar-cell__events">
              {index % 5 === 2 ? <SkeletonPill width="82%" height={14} /> : null}
              {index % 7 === 3 ? <SkeletonPill width="60%" height={14} /> : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ConciergeApplicationTableSkeleton({ rows = 6 }) {
  const columns = [
    { key: 'id', variant: 'line', width: '54%' },
    { key: 'applicant', variant: 'profile', size: 28, primaryWidth: '64%', secondaryWidth: '42%' },
    { key: 'contact', variant: 'stack', primaryWidth: '72%', secondaryWidth: '54%' },
    { key: 'event', variant: 'line', width: '76%' },
    { key: 'date', variant: 'line', width: '62%' },
    { key: 'status', variant: 'pill', width: 72, height: 22 },
    { key: 'actions', variant: 'actions', widths: [54, 54], height: 30 },
  ];
  return (
    <div className="admin-concierge-table-wrapper" aria-hidden="true">
      <table className="admin-concierge-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>申請者</th>
            <th>連絡先</th>
            <th>イベント名</th>
            <th>開催日</th>
            <th>状態</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <TableSkeletonRows rows={rows} columns={columns} />
        </tbody>
      </table>
    </div>
  );
}

export function SettingsCardSkeleton({ className = 'card', rows = 3 }) {
  return (
    <div className={className} role="status" aria-live="polite">
      <div className="content-skeleton">
        <SkeletonLine width="46%" height={16} />
        <SkeletonLine width="86%" height={12} />
        {Array.from({ length: rows }, (_, index) => (
          <div key={`settings-card-skeleton-${index}`} className="content-skeleton__setting-row">
            <div>
              <SkeletonLine width={index === 1 ? 138 : 168} height={13} />
              <SkeletonLine width={index === 2 ? 112 : 196} height={10} />
            </div>
            <SkeletonButton width={58} height={30} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function PdfPreviewSkeleton({ pages = 2 }) {
  return (
    <div className="pdf-view pdf-view--skeleton" role="status" aria-live="polite">
      {Array.from({ length: pages }, (_, index) => (
        <div key={`pdf-preview-skeleton-${index}`} className="content-skeleton__pdf-page">
          <SkeletonLine width="42%" height={18} />
          <SkeletonLine width="88%" height={12} />
          <SkeletonLine width="76%" height={12} />
          <SkeletonBlock className="content-skeleton__pdf-table" />
          <SkeletonLine width="62%" height={12} />
        </div>
      ))}
    </div>
  );
}

export function FinanceYearTableSkeleton({ rows = 5 }) {
  const columns = [
    { key: 'year', variant: 'line', width: '48%' },
    { key: 'artistFee', variant: 'line', width: '72%', align: 'right' },
    { key: 'revenue', variant: 'line', width: '68%', align: 'right' },
    { key: 'expense', variant: 'line', width: '68%', align: 'right' },
    { key: 'profit', variant: 'line', width: '62%', align: 'right' },
    { key: 'requests', variant: 'line', width: '42%', align: 'right' },
    { key: 'artists', variant: 'line', width: '42%', align: 'right' },
  ];
  return (
    <table className="af-table" aria-hidden="true">
      <thead>
        <tr>
          <th style={{ width: 120 }}>年</th>
          <th className="af-num">支出（アーティスト報酬）合計</th>
          <th className="af-num">収入合計</th>
          <th className="af-num">支出合計</th>
          <th className="af-num">収支</th>
          <th className="af-num">依頼数</th>
          <th className="af-num">アーティスト人数</th>
        </tr>
      </thead>
      <tbody>
        <TableSkeletonRows rows={rows} columns={columns} rowClassName="af-clickable" />
      </tbody>
    </table>
  );
}

export function TicketSalesOverviewSkeleton({ rows = 4 }) {
  return (
    <div className="ticket-sales-card-list" aria-hidden="true">
      {Array.from({ length: rows }, (_, index) => (
        <article key={`ticket-sales-card-skeleton-${index}`} className="ticket-sales-card">
          <div className="ticket-sales-card__header">
            <SkeletonLine className="ticket-sales-card__date" width={86} height={12} />
            <SkeletonPill width={74} height={22} />
          </div>
          <SkeletonLine className="ticket-sales-card__title" width={index === 1 ? '58%' : '78%'} height={16} />
          <SkeletonLine className="ticket-sales-card__subtitle" width={index === 2 ? '42%' : '64%'} height={11} />
          <div className="ticket-sales-card__progress">
            <div className="ticket-sales-card__progress-row">
              <SkeletonLine width={54} height={11} />
              <SkeletonBlock className="content-skeleton__bar" />
              <SkeletonLine width={38} height={11} />
            </div>
          </div>
          <div className="ticket-sales-card__metrics">
            <SkeletonLine width={72} height={11} />
            <SkeletonLine width={72} height={11} />
            <SkeletonLine width={72} height={11} />
          </div>
        </article>
      ))}
    </div>
  );
}

// 券売状況分析ページ（リデザイン後）の構造に一致するスケルトン。
// グラフ部分は面＋線のシルエット＋シマーで「読み込み中のグラフ」として見せる。
export function TicketAnalyticsSkeleton() {
  return (
    <div aria-hidden="true">
      {/* ヒーロー: 販売推移グラフ */}
      <section className="taa-card taa-chart-card">
        <div className="taa-chart-head">
          <div className="taa-chart-title-group">
            <SkeletonLine width={88} height={16} />
            <SkeletonPill width={96} height={30} radius={8} />
            <SkeletonPill width={128} height={30} radius={8} />
          </div>
          <SkeletonPill width={232} height={30} radius={8} />
        </div>
        <div className="taa-chart-meta">
          <SkeletonLine width={150} height={12} />
          <SkeletonLine width={112} height={12} />
          <SkeletonLine width={118} height={12} />
        </div>
        <div className="taa-chart-skel">
          <svg className="taa-chart-skel__svg" viewBox="0 0 900 300" role="img" aria-hidden="true">
            <line className="taa-chart-skel__grid" x1="48" y1="18" x2="884" y2="18" />
            <line className="taa-chart-skel__grid" x1="48" y1="142" x2="884" y2="142" />
            <line className="taa-chart-skel__grid" x1="48" y1="266" x2="884" y2="266" />
            <path
              className="taa-chart-skel__area"
              d="M48 232 C 168 226, 250 150, 360 178 S 588 150, 690 96 S 840 52, 884 62 L 884 266 L 48 266 Z"
            />
            <path
              className="taa-chart-skel__line"
              d="M48 232 C 168 226, 250 150, 360 178 S 588 150, 690 96 S 840 52, 884 62"
            />
            <circle className="taa-chart-skel__dot" cx="884" cy="62" r="5" />
          </svg>
          <span className="taa-chart-skel__sweep" />
        </div>
      </section>

      {/* KPI ストリップ */}
      <section className="taa-kpi-strip">
        {[0, 1, 2, 3, 4].map((index) => (
          <div key={`taa-kpi-skeleton-${index}`} className="taa-kpi">
            <SkeletonLine width={index === 4 ? '58%' : '46%'} height={11} />
            <SkeletonLine width={index === 0 ? 84 : 104} height={24} style={{ marginTop: 8 }} />
            {index === 0 ? (
              <SkeletonBlock height={7} radius={999} style={{ marginTop: 9 }} />
            ) : null}
            <SkeletonLine width="62%" height={10} style={{ marginTop: index === 0 ? 9 : 4 }} />
          </div>
        ))}
      </section>

      {/* 2 カラム: 販路構成 / チケット種別 */}
      <div className="taa-two-col">
        <section className="taa-card">
          <div className="taa-section-h">
            <SkeletonLine width={88} height={14} />
            <SkeletonLine width={118} height={11} />
          </div>
          <SkeletonBlock height={16} radius={8} style={{ marginBottom: 14 }} />
          <div className="taa-legend">
            {[0, 1, 2, 3].map((index) => (
              <div key={`taa-legend-skeleton-${index}`} className="taa-legend-row">
                <SkeletonBlock width={10} height={10} radius={3} />
                <SkeletonLine width={index % 2 ? '52%' : '40%'} height={12} />
                <SkeletonLine width={72} height={11} />
              </div>
            ))}
          </div>
        </section>
        <section className="taa-card">
          <div className="taa-section-h">
            <SkeletonLine width={104} height={14} />
            <SkeletonLine width={96} height={11} />
          </div>
          <div className="taa-type-list">
            {[0, 1, 2].map((index) => (
              <div key={`taa-type-skeleton-${index}`} className="taa-type-row">
                <div className="taa-type-top">
                  <SkeletonLine width={index === 2 ? '36%' : '46%'} height={13} />
                  <SkeletonLine width={128} height={11} />
                </div>
                <SkeletonBlock height={8} radius={999} />
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* タブ集約: 日別 / 公演別 / 販路×種別 */}
      <section className="taa-card taa-detail-card">
        <div className="taa-tabs">
          {[64, 64, 88].map((width, index) => (
            <span key={`taa-tab-skeleton-${index}`} className="taa-skel-tab">
              <SkeletonLine width={width} height={14} />
            </span>
          ))}
        </div>
        <div className="taa-panel">
          <SkeletonLine width="46%" height={11} style={{ margin: '8px 2px 12px' }} />
          <table className="taa-table">
            <thead>
              <tr>
                {[0, 1, 2, 3].map((index) => (
                  <th key={`taa-th-skeleton-${index}`} className={index === 0 ? undefined : 'num'}>
                    <SkeletonLine
                      width={index === 0 ? 48 : 72}
                      height={11}
                      style={index === 0 ? undefined : { marginLeft: 'auto' }}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 6 }, (_, rowIndex) => (
                <tr key={`taa-tr-skeleton-${rowIndex}`}>
                  <td>
                    <SkeletonLine width={rowIndex % 3 === 1 ? 68 : 84} height={12} />
                  </td>
                  <td className="num">
                    <SkeletonLine width={56} height={12} style={{ marginLeft: 'auto' }} />
                  </td>
                  <td className="num">
                    <SkeletonLine width={80} height={12} style={{ marginLeft: 'auto' }} />
                  </td>
                  <td className="num">
                    <SkeletonLine width={80} height={12} style={{ marginLeft: 'auto' }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

export function EventTicketSalesFormSkeleton() {
  return (
    <div className="event-ticket-sales-content" role="status" aria-live="polite">
      <div className="event-ticket-card">
        <SkeletonLine width="48%" height={18} />
        <SkeletonLine width="82%" height={12} />
      </div>
      <div className="event-ticket-sales-panel">
        <div className="panel-heading">
          <SkeletonLine width={156} height={16} />
          <SkeletonButton width={92} height={34} />
        </div>
        <div className="entries-table">
          {[0, 1, 2, 3].map((index) => (
            <div key={`ticket-sales-form-row-skeleton-${index}`} className="entry-row">
              <SkeletonLine width={index === 1 ? '52%' : '72%'} height={12} />
              <SkeletonLine width="58%" height={12} />
              <SkeletonButton width={64} height={30} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function FormSkeleton({
  title = false,
  sections = 3,
  fieldsPerSection = 3,
  columns = 1,
  showSubmit = false,
  className = '',
}) {
  const sectionArr = Array.from({ length: Math.max(1, sections) });
  return (
    <div
      className={[
        'content-skeleton__form',
        columns === 2 ? 'content-skeleton__form--two' : '',
        className,
      ].filter(Boolean).join(' ')}
      role="status"
      aria-live="polite"
    >
      {title ? <SkeletonLine className="content-skeleton__form-title" width={220} height={20} /> : null}
      <div className="content-skeleton__form-grid">
        {sectionArr.map((_, s) => (
          <div key={`form-skeleton-card-${s}`} className="content-skeleton__form-card" aria-hidden="true">
            <SkeletonLine width={s % 2 === 1 ? '34%' : '46%'} height={15} />
            {Array.from({ length: Math.max(1, fieldsPerSection) }, (_, f) => (
              <div key={`form-skeleton-field-${s}-${f}`} className="content-skeleton__field">
                <SkeletonLine width={96 + (f % 3) * 26} height={11} />
                <SkeletonBlock className="content-skeleton__field-input" />
              </div>
            ))}
          </div>
        ))}
      </div>
      {showSubmit ? <SkeletonButton className="content-skeleton__form-submit" width={180} height={42} /> : null}
    </div>
  );
}

export function KeyValueTableSkeleton({ rows = 8, title = true, viewClassName = 'venue-detail-view', className = '' }) {
  return (
    <div className={['content-skeleton__kv', className].filter(Boolean).join(' ')} role="status" aria-live="polite">
      {title ? <SkeletonLine className="content-skeleton__kv-title" width={160} height={20} /> : null}
      <div className={viewClassName} aria-hidden="true">
        <table>
          <tbody>
            {Array.from({ length: Math.max(1, rows) }, (_, index) => (
              <tr key={`kv-skeleton-row-${index}`}>
                <th><SkeletonLine width={index % 3 === 1 ? 56 : 72} height={12} /></th>
                <td>
                  {index === 3 ? (
                    <SkeletonBlock className="content-skeleton__kv-media" />
                  ) : (
                    <SkeletonLine width={index % 2 === 0 ? '62%' : '44%'} height={12} />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function SimpleTableSkeleton({
  columns = 3,
  rows = 6,
  headers = null,
  title = false,
  tableClassName = 'content-skeleton__table',
  className = '',
}) {
  const colCount = Array.isArray(headers) && headers.length ? headers.length : Math.max(1, columns);
  const cols = Array.from({ length: colCount });
  return (
    <div className={['content-skeleton__table-block', className].filter(Boolean).join(' ')} role="status" aria-live="polite">
      {title ? <SkeletonLine className="content-skeleton__table-title" width={200} height={18} /> : null}
      <table className={tableClassName} aria-hidden="true">
        {Array.isArray(headers) && headers.length ? (
          <thead>
            <tr>
              {headers.map((label, index) => (
                <th key={`simple-table-head-${index}`}>{label}</th>
              ))}
            </tr>
          </thead>
        ) : null}
        <tbody>
          {Array.from({ length: Math.max(1, rows) }, (_, r) => (
            <tr key={`simple-table-row-${r}`}>
              {cols.map((_, c) => (
                <td key={`simple-table-cell-${r}-${c}`}>
                  <SkeletonLine width={c === 0 ? '72%' : `${44 + ((r + c) % 3) * 14}%`} height={12} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function WorkspaceCanvasSkeleton({ sidebarRows = 6, className = '' }) {
  return (
    <div className={['content-skeleton__workspace', className].filter(Boolean).join(' ')} role="status" aria-live="polite">
      <aside className="content-skeleton__workspace-sidebar" aria-hidden="true">
        <SkeletonLine width="54%" height={13} />
        {Array.from({ length: Math.max(1, sidebarRows) }, (_, index) => (
          <div key={`workspace-skeleton-row-${index}`} className="content-skeleton__workspace-row">
            <SkeletonLine width={index % 2 === 0 ? '78%' : '58%'} height={11} />
          </div>
        ))}
      </aside>
      <div className="content-skeleton__workspace-canvas" aria-hidden="true">
        <SkeletonBlock className="content-skeleton__workspace-canvas-inner" />
      </div>
      <span className="sr-only">レイアウトを読み込み中...</span>
    </div>
  );
}

export function TimetableDesignerSkeleton({ rows = 8, columns = 6 }) {
  const headerCols = Array.from({ length: Math.max(2, columns) });
  return (
    <div className="content-skeleton__timetable" role="status" aria-live="polite">
      <div className="content-skeleton__timetable-tabs" aria-hidden="true">
        <SkeletonPill width={96} height={30} />
        <SkeletonPill width={96} height={30} />
        <SkeletonPill width={40} height={30} />
      </div>
      <div className="content-skeleton__timetable-toolbar" aria-hidden="true">
        <SkeletonButton width={72} height={30} />
        <SkeletonButton width={72} height={30} />
        <SkeletonButton width={54} height={30} />
        <SkeletonButton width={54} height={30} />
        <SkeletonButton width={90} height={30} />
      </div>
      <div className="content-skeleton__timetable-wrap" aria-hidden="true">
        <table className="content-skeleton__timetable-table">
          <thead>
            <tr>
              {headerCols.map((_, index) => (
                <th key={`timetable-skeleton-head-${index}`}>
                  <SkeletonLine width={index === 0 ? 48 : '62%'} height={11} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: Math.max(1, rows) }, (_, r) => (
              <tr key={`timetable-skeleton-row-${r}`}>
                {headerCols.map((_, c) => (
                  <td key={`timetable-skeleton-cell-${r}-${c}`}>
                    <SkeletonLine width={c === 0 ? 44 : `${46 + ((r + c) % 3) * 16}%`} height={11} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function ParticipantInfoTableSkeleton({ columns = [], rows = 6 }) {
  const safeColumns = Array.isArray(columns) && columns.length > 0
    ? columns
    : [
        { id: 'artist', label: 'アーティスト' },
        { id: 'status', label: 'ステータス' },
        { id: 'fee', label: '金額' },
      ];
  const skeletonColumns = safeColumns.map((field, index) => ({
    key: field.id || index,
    variant: index === 0 ? 'profile' : index % 4 === 0 ? 'pill' : 'line',
    size: 28,
    width: index % 3 === 0 ? '62%' : '74%',
    primaryWidth: '66%',
    secondaryWidth: '38%',
  }));
  return (
    <table className="participant-table" aria-hidden="true">
      <thead>
        <tr>
          {safeColumns.map((field, index) => (
            <th key={field.id || index}>{field.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        <TableSkeletonRows rows={rows} columns={skeletonColumns} />
      </tbody>
    </table>
  );
}
